// src/components/Sidebar.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logoutUser } from "../services/firebaseAuthService";
import { getModulePermissions } from "../services/firebaseModuleService";
import { getSidebarOrder } from '../services/firebaseModuleService';
import './Sidebar.css';
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useUser } from '../UserContext';
import ProjektSelector from './ProjektSelector';

const Sidebar = () => {
  const userContext = useUser() || {};
  const { user: currentUser } = userContext;
  const [permissions, setPermissions] = useState({
    dienstuebergabe: false,
    staerkemeldung: false,
    aufgaben: false,
    feuerloescher: false,
    engeRaeume: false,
    berichte: false,
    dienstplan: false,
    freiwuenscheUrlaub: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // Berechtigungen aus Firebase laden mit useCallback
  const loadPermissions = useCallback(() => {
    try {
      const docRef = doc(db, "modulePermissions", "settings");
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          setPermissions(docSnap.data());
        }
        setIsLoading(false);
      }, (error) => {
        console.error("Fehler beim Laden der Modulberechtigungen:", error);
        setIsLoading(false);
      });
      return unsubscribe;
    } catch (error) {
      console.error("Fehler beim Laden der Modulberechtigungen:", error);
      setIsLoading(false);
      return undefined;
    }
  }, []);

  // Beim Laden den aktuellen Benutzer und Berechtigungen abrufen
  useEffect(() => {
    const unsubscribe = loadPermissions();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [loadPermissions]);

  // Logout-Funktion
  const handleLogout = async () => {
    try {
      await logoutUser();
      localStorage.removeItem("currentUser");
      localStorage.setItem("isLoggedIn", "false");
      navigate("/login");
    } catch (error) {
      console.error("Fehler beim Logout:", error);
      alert("Fehler beim Abmelden: " + error.message);
    }
  };

  // Hilfsfunktion, um den Rollennamen anzuzeigen
  const getRoleName = (role) => {
    switch (role) {
      case "superadmin": return "SuperAdmin";
      case "admin": return "Admin";
      case "user": return "Benutzer";
      default: return role;
    }
  };

  // Prüft, ob der Benutzer Admin-Rechte hat (Admin oder SuperAdmin)
  const hasAdminRights = () => {
    return currentUser && (currentUser.role === "admin" || currentUser.role === "superadmin");
  };

  // Prüft, ob der Benutzer SuperAdmin ist
  const isSuperAdmin = () => {
    return currentUser && currentUser.role === "superadmin";
  };

  // Standard-Reihenfolge der Navigation
  const defaultNavOrder = [
    { id: 'dashboard', path: '/', icon: 'fas fa-home', label: 'Dashboard', module: 'dashboard' },
    { id: 'dienstuebergabe', path: '/dienstuebergabe', icon: 'fas fa-exchange-alt', label: 'Dienstübergabe', module: 'dienstuebergabe' },
    { id: 'staerkemeldung', path: '/staerkemeldung', icon: 'fas fa-users', label: 'Stärkemeldung', module: 'staerkemeldung' },
    { id: 'aufgaben', path: '/aufgaben', icon: 'fas fa-tasks', label: 'Aufgaben', module: 'aufgaben' },
    { id: 'feuerloescher', path: '/feuerloescher', icon: 'fas fa-fire-extinguisher', label: 'Feuerlöscher', module: 'feuerloescher' },
    { id: 'engeRaeume', path: '/enge-raeume', icon: 'fas fa-door-closed', label: 'Enge Räume', module: 'engeRaeume' },
    { id: 'berichte', path: '/berichte', icon: 'fas fa-file-alt', label: 'Berichte', module: 'berichte' },
    { id: 'dienstplan', path: '/dienstplan', icon: 'fas fa-calendar-alt', label: 'Dienstplan', module: 'dienstplan' },
    { id: 'staffomatic', path: '/staffomatic', icon: 'fas fa-mobile-alt', label: 'Staffomatic', module: 'staffomatic' },
    { id: 'freiwuenscheUrlaub', path: '/freiwuensche-urlaub', icon: 'fas fa-calendar-check', label: 'Freiwünsche & Urlaub', module: 'freiwuenscheUrlaub' },
    { id: 'passwort', path: '/passwort', icon: 'fas fa-key', label: 'Passwort', module: 'passwort' },
    { id: 'speicherorte', path: '/speicherorte', icon: 'fas fa-save', label: 'Speicherorte', module: 'speicherorte' },
    { id: 'einstellungen', path: '/einstellungen', icon: 'fas fa-cog', label: 'Einstellungen', module: 'einstellungen' },
    { id: 'benutzerverwaltung', path: '/benutzerverwaltung', icon: 'fas fa-users-cog', label: 'Benutzerverwaltung', module: 'benutzerverwaltung', adminOnly: true }
  ];

  // Benutzerdefinierte Reihenfolge aus localStorage laden
  const [navOrder, setNavOrder] = useState(defaultNavOrder);

  // Synchronisiere navOrder mit Änderungen im LocalStorage (z.B. durch Einstellungen-Seite)
  useEffect(() => {
    // Lade globale Reihenfolge aus Firestore
    getSidebarOrder().then(orderFromDb => {
      if (orderFromDb && Array.isArray(orderFromDb)) {
        const sorted = orderFromDb.map(id => defaultNavOrder.find(item => item.id === id)).filter(Boolean);
        const missing = defaultNavOrder.filter(item => !orderFromDb.includes(item.id));
        setNavOrder([...sorted, ...missing]);
      } else {
        setNavOrder(defaultNavOrder);
      }
    });
  }, []);

  // Prüft, ob ein Modul für den aktuellen Benutzer sichtbar sein soll
  const isModuleVisible = (moduleName) => {
    // Nur SuperAdmin sieht immer alle Module
    if (isSuperAdmin()) {
      return true;
    }
    
    // Admin und normale Benutzer sehen nur Module, die für ihre Rolle freigeschaltet sind
    const modulePermission = permissions[moduleName];
    if (!modulePermission) return false;
    
    // Sicherstellen, dass sichtbarkeit ein Array ist (Migration)
    const sichtbarkeit = Array.isArray(modulePermission.sichtbarkeit) 
      ? modulePermission.sichtbarkeit 
      : [modulePermission.sichtbarkeit];
    
    const result = sichtbarkeit.includes(currentUser.role);
    if(moduleName === 'einstellungen') {
      console.log('[DEBUG Sichtbarkeit Einstellungen]', {
        sichtbarkeit,
        currentUserRole: currentUser.role,
        result
      });
    }
    return result;
  };



  // Hole aktuellen Projektnamen aus localStorage
  let projektName = '';
  try {
    const projektId = localStorage.getItem('lastProjektId');
    const projekteRaw = localStorage.getItem('projekte');
    console.log('DEBUG Sidebar: lastProjektId =', projektId);
    console.log('DEBUG Sidebar: projekte =', projekteRaw);
    if (projektId && projekteRaw) {
      const projekte = JSON.parse(projekteRaw);
      const found = projekte.find(p => p.id === projektId);
      if (found) projektName = found.name;
    }
  } catch (e) { console.error('DEBUG Sidebar: Fehler beim Parsen der Projekte', e); }
  // Debug-Log für den gefundenen Projektnamen
  console.log('DEBUG Sidebar: projektName =', projektName);

  // Vor dem Rendern der Navigation debuggen
  console.log('[DEBUG navOrder]', navOrder);

  // Wenn kein Benutzer eingeloggt ist, leere Sidebar anzeigen
  if (!currentUser) {
    return <div className="sidebar"></div>;
  }

  // Wenn Module noch geladen werden, minimale Sidebar anzeigen
  if (isLoading) {
    return (
      <div className="sidebar">
        <div className="user-info">
          <span className="username">{currentUser.username}</span>
          <span className={`role role-${currentUser.role}`}>{getRoleName(currentUser.role)}</span>
        </div>
        <p className="loading-message">Modulberechtigungen werden geladen...</p>
      </div>
    );
  }

  return (
    <div className="sidebar">
      {/* Power-Logo ganz oben */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '18px 0 8px 0' }}>
        <img 
          src="/logo.png" 
          alt="Power Logo" 
          style={{ width: '230px', height: '110px', objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      </div>
      {/* ProjektSelector */}
      <div style={{ padding: '0 16px', marginBottom: '16px' }}>
        <ProjektSelector />
      </div>
      {/* Benutzerinformation */}
      <div className="user-info" style={{display:'flex',flexDirection:'column',alignItems:'flex-start',margin:'8px 0 16px 0'}}>
        <span className={`role role-${currentUser.role}`} style={{fontWeight:700,fontSize:'1em',color:'#888',marginBottom:2}}>{getRoleName(currentUser.role)}</span>
        <span className="username" style={{fontWeight:600,fontSize:'1.1em',color:'#fff'}}>{currentUser.username}</span>
      </div>
      


      {/* Hauptnavigation */}
      <nav className="sidebar-nav">
        {navOrder.map((item, index) => {
          // Prüfen ob das Element angezeigt werden soll
          const shouldShow = 
            (!item.module || isModuleVisible(item.module)) && 
            (!item.adminOnly || hasAdminRights());

          if (!shouldShow) return null;

          return (
            <Link key={item.id} to={item.path} className="nav-item">
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      
      {/* Logout-Button ganz unten */}
      <div className="logout-btn-container">
        <button className="logout-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i>
          <span>Abmelden</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;